// =============================================================================
// useMatchDetail — fetch `/feed/{slug}/match/{eventId}`.
// =============================================================================
// Per-event hook used by the match detail screen. Aborts in-flight calls on
// unmount or when `eventId` changes. Returns the raw envelope; widget
// components narrow `widgets[].data` themselves.
//
// Self-healing retry policy (2026-05-20)
// ----------------------------------------
// Cold-cache fetches against VS Feeds upstream can take 15-20s and
// occasionally time out. The user must NEVER see "Could not load this
// match" for a transient hiccup — they'll bounce off the site before
// noticing a retry button. So this hook does silent auto-retry:
//
//   - 3 attempts total, backoff 500ms → 1500ms → 4000ms
//   - loading=true stays true across all attempts; user sees the loading
//     spinner the whole time, not a flash of error
//   - error is only surfaced if ALL 3 attempts fail
//   - Caller-initiated aborts (component unmount, eventId change) bail
//     immediately — they're not "errors" to retry
//   - Client-level retry is disabled (retries: 0) so we control the full
//     attempt budget here — no double-retry compounding
//
// Engine-side degraded mode (Lag 2)
// ----------------------------------
// When upstream + LKG both fail, the engine returns 200 with
// `degradedMode: true` and an empty widgets array. We treat that the
// same as a transient error inside the initial retry loop — the goal
// is to land a full response. After the retry budget is exhausted, we
// surface the degraded data anyway (render shows match header from its
// own fixtures cache + a soft "loading live data" state) AND continue
// polling refresh() in the background every 30s until a full response
// lands. The user never sees an error page, and the page self-heals.
//
// Total worst-case wait: 3 × 15s timeout + 6s backoff = ~51s. That's
// only reached when VS Feeds is fully down; first cached attempt
// returns in <100ms.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MatchDetailResponse } from '@aamp/shared-types';
import { useEngineClient } from '../api/EngineClientProvider.js';
import { EngineFetchError } from '../api/createEngineClient.js';
import { reportError } from '../observability.js';

interface State {
  data: MatchDetailResponse | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const RETRY_BACKOFFS_MS = [500, 1500, 4000] as const;

/** True if the error is worth retrying. 404 / 4xx user errors are not. */
function isTransient(err: unknown): boolean {
  // AbortError from our own controller (timeout fired): transient.
  // AbortError from the OUTER signal (unmount / eventId change): NOT
  // transient — caller decided to bail. We can't easily distinguish at
  // this point, so the call site checks generation ref instead.
  if (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError')) {
    return true;
  }
  // #472 — Use instanceof on the typed error rather than `as { status }`.
  // The cast worked but silently broke if the engine ever changed its
  // error shape; instanceof binds the check to the class so a future
  // rename surfaces as a TypeScript error instead of stale logic.
  if (err instanceof EngineFetchError) {
    // Status 0 = network failure; 5xx = transient upstream. Everything
    // else (401/403/404/422) is the user's bad input and won't get
    // fixed by retrying.
    return err.status === 0 || err.status >= 500;
  }
  // Unknown error shape — treat as transient (network failure, JSON parse
  // error mid-stream, etc.). Better to retry once than to surface an
  // opaque error to the user.
  return true;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    if (signal.aborted) {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Phase 5G — optional marketFocus forwards `?market=X` to the engine.
 * When set + the tenant has features_layout_planner_enabled=TRUE, the
 * response carries an extra `marketLayout` field that the screen
 * prepends above the IHS bundle. Absent => bundle-only (existing path).
 */
export function useMatchDetail(
  eventId: string | null | undefined,
  marketFocus?: string,
): State {
  const client = useEngineClient();

  const [data, setData] = useState<MatchDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();

    if (!eventId) {
      setLoading(false);
      setError(null);
      setData(null);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const gen = generationRef.current + 1;
    generationRef.current = gen;

    setLoading(true);
    setError(null);

    let lastError: unknown;
    let lastDegraded: MatchDetailResponse | null = null;
    const totalAttempts = RETRY_BACKOFFS_MS.length;

    for (let attempt = 0; attempt < totalAttempts; attempt += 1) {
      // Stale generation — caller already moved on (eventId change or
      // unmount). Bail without setting state.
      if (gen !== generationRef.current) return;
      if (controller.signal.aborted) return;

      try {
        const resp = await client.fetchMatchDetail(eventId, {
          signal: controller.signal,
          // Disable client-level retry — we own the full attempt budget
          // here so backoff timings are predictable and not doubled.
          retries: 0,
          // Phase 5G — forward ?market=X to the engine when caller asked
          // for a market focus. Engine uses this to decide whether to
          // enrich the response with marketLayout (LayoutPlan).
          ...(marketFocus ? { marketFocus } : {}),
        });
        if (gen !== generationRef.current) return;

        // Engine flagged this as a degraded stub (Lag 2 fallback) —
        // upstream + LKG both failed. Treat as transient: keep
        // retrying in the hope the next attempt lands a full body.
        // Remember the stub so we can surface it if all retries fail.
        if (resp.degradedMode === true) {
          lastDegraded = resp;
          // Fall through to backoff-and-retry below.
        } else {
          // Full success.
          setData(resp);
          setLoading(false);
          return;
        }
      } catch (err) {
        // Outer abort (component unmount / eventId change) — bail
        // silently. The new mount will run a fresh load.
        if (controller.signal.aborted) return;
        if (gen !== generationRef.current) return;

        lastError = err;

        // Non-transient errors (404, 401) surface immediately — no
        // amount of retry will fix them.
        if (!isTransient(err)) break;
      }

      // Either degraded response or transient error — back off and try
      // again, unless we've used our budget. Jitter (audit fix #468):
      // without random jitter, 100 concurrent users on a cold popular
      // match all retry at the SAME ms offsets, hammering the engine
      // in synchronised waves. Add up to 50% extra delay (capped at
      // 1s) so retries spread out. Bounded by the original budget +
      // 50% so worst-case wait time only grows from ~51s to ~76s in
      // the rare all-failures case.
      const baseBackoff = RETRY_BACKOFFS_MS[attempt + 1];
      if (attempt < totalAttempts - 1 && typeof baseBackoff === 'number') {
        const jitterMs = Math.random() * Math.min(baseBackoff * 0.5, 1000);
        try {
          await sleep(baseBackoff + jitterMs, controller.signal);
        } catch {
          // Sleep aborted (unmount) — bail.
          return;
        }
      }
    }

    // All attempts exhausted.
    if (gen !== generationRef.current) return;

    // Preference order for what to surface:
    //   1. Degraded stub from the engine — render uses its fixtures
    //      cache for the match header and the page self-heals on the
    //      polling cycle below (no error UI, no retry button).
    //   2. Genuine non-transient error (404 etc.) — surface as error.
    //   3. Transient error with no degraded stub — surface as error.
    if (lastDegraded) {
      setData(lastDegraded);
      setLoading(false);
      return;
    }
    const e = lastError instanceof Error ? lastError : new Error('Match detail fetch failed');
    setError(e);
    setLoading(false);
    reportError(e, { scope: 'useMatchDetail', eventId, attempts: totalAttempts });
  }, [client, eventId, marketFocus]);

  useEffect(() => {
    void load();
    return (): void => abortRef.current?.abort();
  }, [load]);

  // Background self-heal: while the data we have is a degraded stub,
  // refetch every 30s until a full response lands. This is the
  // "page fixes itself" half of Lag 2 — the user never has to click
  // retry; they just watch the live data appear when upstream recovers.
  // Stops automatically once data.degradedMode is no longer true.
  useEffect(() => {
    if (data?.degradedMode !== true) return;
    const id = setInterval(() => {
      void load();
    }, 30_000);
    return (): void => clearInterval(id);
  }, [data?.degradedMode, load]);

  return { data, loading, error, refresh: load };
}
